﻿import { MainSite, ITimeZoneData } from "../../MainSite";

export interface DateRangePickerData {
    from: moment.Moment;
    to: moment.Moment;
    tzName: string;
    tzOffset: string;
    isValid: boolean;
    hasRange: boolean;
    lastUpdateSource?: string;
    lastChangedIncludedNow: boolean;
    lastClickedDropdownItemId?: string;
    debug?: {
        f: string;
        t: string;
    }
}

export interface DateRangePickerOptions {
    /** If true and an input field (from/to dates) is changed, then the time is reset to from=00:00:00 and to=23:59:59 */
    onManualChangeDateUpdateTime: boolean;
}

export class DateRangePicker {
    /**
     * DEBUGGING INFO: In Chrome console, add the following items (in order) to watch/live expressions: (NB: this assumes we're working on PVD chart in SiteObservations/Current page..)
     * 
     * - P = Icon.Midi.WebApplication.FrontEnd.MainSite.instance.pages[3].pages[3]; //: gets the current page.
     * - [P._pvd.selectedHours, P._pvd.drp.getLastIncludedNow(), P._pvd.drp.timeFrom, P._pvd.drp.timeTo] //: an array of helpful live items.
     */



    /** The default format for debugging Moment objects */
    public static MOMENT_DEBUG_FORMAT = "YYYY-MM-DD HH:mm:ss ZZ (x)";

    private rendered: boolean = false;
    private preventUpdateEvents: boolean = false; //: a flag to prevent calling update events, in case we need to make sure all values are set before calling these.
    private lastUpdateSource: string = null;
    private lastClickedDropdownItemId: string = null;
    private lastChangedIncludedNow: boolean = false; //: a flag to keep if the values provided with .setValues() did include the now moment or not (so if we run an update of live data for example, the range could be automatically updated to keep including the now moment.)

    /**
     * This helper flag is set to true if the 'changeDate' event is triggered via .setValues() or .setTimezone() methods.
     * If it is false, assume that the 'changeDate' event was caused by the user modifying the values manually in the inputs.
     * */
    public onChangeDateViaSetValuesMethods: boolean = false; //: an internal helper flag, to help determine the reason .datepicker is triggering 'changeDate' event (if via .setValues() or because the dates were manually modified in the fields (from, to, timezone))

    private $datepicker: JQuery;
    private $inputFrom: JQuery;
    private $inputTo: JQuery;
    private $inputTimezone: JQuery;
    private $displayTimezone: JQuery;
    private $dropdownWrap: JQuery = null;
    private $dropdownBtn: JQuery = null;

    public timeFrom: string = "00:00:00";
    public timeTo: string = "23:59:59";

    public options: DateRangePickerOptions = { //: NB: maybe this should be extended by a param of constructor, but at time of writing this was not yet necessary.
        onManualChangeDateUpdateTime: true
    };

    constructor(private mainSite: MainSite, private $container: JQuery, private postUpdateEvent: (drp: DateRangePicker, updateSource: string) => void = null) {
    }

    /**
     * Check the validity of a date provided as YYYY-MM-DD string.
     * @param Ymd The string to check.
     */
    public static checkYmdString(Ymd: string): boolean {
        let m = moment(Ymd, "YYYY-MM-DD", true);
        return m && m.isValid();
    }

    public getSelectedTimezoneData(): ITimeZoneData {
        let data: ITimeZoneData = null;
        try {
            let $opt: JQuery = this.$inputTimezone.find('option[value="' + this.$inputTimezone.val() + '"]');
            let tzd = $opt.data("tzd");
            if (tzd) {
                data = tzd as ITimeZoneData;
            }
        } catch (err) {
            let tzOpts: ITimeZoneData[] = this.mainSite.getAllAvailableTimeZones();
            data = tzOpts.filter(x => x.timeZoneName == this.mainSite.getTimeZoneName())[0];
        }
        return data;
    }

    public setReadonlyInputs(readonly: boolean = false): void {
        if (!this.rendered) {
            console.error("DateRangePicker ERROR: the elements have not yet been rendered. Please render them prior to setting inputs to readonly (or not).");
            return;
        }

        this.$inputFrom.prop('readonly', readonly);
        this.$inputTo.prop('readonly', readonly);
        this.$inputTimezone.prop('readonly', readonly);
    }

    public getData(): DateRangePickerData {
        if (!this.rendered) {
            console.error("DateRangePicker ERROR: the elements have not yet been rendered. Please render them prior to getting data.");
            return;
        }

        let tzd = this.getSelectedTimezoneData();
        if (tzd == null) {
            return null;
        }

        let offset: string = tzd.offsetString; // this.mainSite.getTimeZoneStringFormat('ZZ');
        let formatParse: string = "YYYY-MM-DD HH:mm:ssZ";
        let formatDebug: string = DateRangePicker.MOMENT_DEBUG_FORMAT;

        //: TODO: if inputs are not valid YYYY-MM-DD, then use invalid moments!

        let f: moment.Moment = DateRangePicker.checkYmdString(this.$inputFrom.val()) ? moment.parseZone(this.$inputFrom.val() + " " + this.timeFrom + offset, formatParse) : moment.invalid();
        let t: moment.Moment = DateRangePicker.checkYmdString(this.$inputTo.val()) ? moment.parseZone(this.$inputTo.val() + " " + this.timeTo + offset, formatParse) : moment.invalid();

        let data: DateRangePickerData = {
            from: f.isValid() ? f : null,
            to: t.isValid() ? t : null,
            tzOffset: offset,
            tzName: tzd.timeZoneName, // this.mainSite.getTimeZoneName(),
            isValid: f.isValid() && t.isValid(),
            hasRange: f.isValid() && t.isValid() && t.isAfter(f),
            lastUpdateSource: this.lastUpdateSource,
            lastChangedIncludedNow: this.lastChangedIncludedNow,
            lastClickedDropdownItemId: this.lastClickedDropdownItemId,
            debug: {
                f: f.isValid() ? f.format(formatDebug) + ` [UTC: ${moment.utc(f.valueOf()).format(formatDebug)}]` : "INVALID",
                t: t.isValid() ? t.format(formatDebug) + ` [UTC: ${moment.utc(t.valueOf()).format(formatDebug)}]` : "INVALID"
            }
        };

        return data;
    }

    /**
     * A simpler method to retrieve the flag for whether last .setValues() included NOW or not, to avoid running range calculations.
     */
    public getLastIncludedNow(): boolean {
        return this.lastChangedIncludedNow;
    }

    /**
     * Helper method to check if the current NOW moment (adapted to selected timezone!) is contained in the selected range.
     * NB: this differs from the .getLastIncludedNow() in that the latter help determine if, at time of setting the range, NOW was included in range set.
     */
    public getCurrentRangeIncludesNow(): boolean {
        if (!this.rendered) {
            console.error("DateRangePicker ERROR: the elements have not yet been rendered. Please render them prior to setting data/values.");
            return;
        }

        let result: boolean = false;

        let tzd: ITimeZoneData = this.getSelectedTimezoneData();
        let now: moment.Moment = moment.utc();
        if (tzd) {
            now = moment.utc().tz(tzd.timeZoneName);
        }

        let data = this.getData();
        if (data && data.isValid) {
            if (now.isBetween(data.from, data.to, 'seconds', '[]')) {
                result = true;
            }
        }

        return result;
    }

    public setTimezone(tzName: string = null, defaultToUTC: boolean = true, runPostUpdateEvent: boolean = true): void {
        if (!this.rendered) {
            console.error("DateRangePicker ERROR: the elements have not yet been rendered. Please render them prior to setting data/values.");
            return;
        }

        this.preventUpdateEvents = true;
        this.onChangeDateViaSetValuesMethods = true;

        if (tzName == null) {
            tzName = this.mainSite.getTimeZoneName();
        }

        let tzOpts: ITimeZoneData[] = this.mainSite.getAllAvailableTimeZones();
        if (tzOpts.filter(x => x.timeZoneName == tzName).length === 1) {
            this.$inputTimezone.val(tzName).trigger("change");
        } else {
            if (tzOpts.filter(x => x.timeZoneName == "UTC").length === 1 && defaultToUTC) {
                this.$inputTimezone.val("UTC").trigger("change");
            } else {
                console.error("Unable to set timezone on date-range picker, as neither the requested timezone nor UTC are available.");
            }
        }

        this.preventUpdateEvents = false;

        if (runPostUpdateEvent) {
            this.triggerUpdate('DateRangePicker.setTimezone');
        }

        this.onChangeDateViaSetValuesMethods = false;
    }

    /**
     * Set the values of the date range picker. Note if either **from** or **to** are set to null or invalid dates (as determined by momoent.isValid()), they will be set to blank, and thus INVALID values.
     * NOTE: if from/to are provided as string (needs to be YYYY-MM-DD formated date), then times are assumed to start at 00:00:00 and end at 23:59:59 (adapted to the timezone in select input)
     * @param from The from date, string in format YYYY-MM-DD, or a valid Moment object
     * @param to The to date, string in format YYYY-MM-DD, or a valid Moment object
     * @param runPostUpdateEvent Set to false if you want to prevent calling the update callbacks (i.e. to prevent some charts updates for example)
     */
    public setValues(from: string | moment.Moment = null, to: string | moment.Moment = null, runPostUpdateEvent: boolean = true, updateSource: string = null): DateRangePickerData {
        if (!this.rendered) {
            console.error("DateRangePicker ERROR: the elements have not yet been rendered. Please render them prior to setting data/values.");
            return;
        }

        let tzd: ITimeZoneData = this.getSelectedTimezoneData();
        let now: moment.Moment = moment.utc();
        if (tzd) {
            now = moment.utc().tz(tzd.timeZoneName);
        }

        // console.warn("DRP .setValues() [BEFORE CHANGES]", { f: from, t: to, ff: moment.isMoment(from) && from.format(DateRangePicker.MOMENT_DEBUG_FORMAT), tf: moment.isMoment(to) && to.format(DateRangePicker.MOMENT_DEBUG_FORMAT), nf: now.format(DateRangePicker.MOMENT_DEBUG_FORMAT), src: updateSource, xx: $.extend(true, {}, this.getData()) });

        this.preventUpdateEvents = true;
        this.onChangeDateViaSetValuesMethods = true;

        this.timeFrom = "00:00:00";
        this.timeTo = "23:59:59";

        if (from !== null && typeof (from) == 'string' && DateRangePicker.checkYmdString(from)) {
            this.$inputFrom.datepicker('setDate', from);
        } else if (from !== null && moment.isMoment(from)) {
            this.$inputFrom.datepicker('setDate', from.format("YYYY-MM-DD"));
            this.timeFrom = from.format("HH:mm:ss");
        } else {
            this.$inputFrom.datepicker('setDate', "");
        }

        if (to !== null && typeof(to) == 'string' && DateRangePicker.checkYmdString(to)) {
            this.$inputTo.datepicker('setDate', to);
        } else if (to !== null && moment.isMoment(to)) {
            this.$inputTo.datepicker('setDate', to.format("YYYY-MM-DD"));
            this.timeTo = to.format("HH:mm:ss");
        } else {
            this.$inputTo.datepicker('setDate', "");
        }

        this.lastChangedIncludedNow = false; //: NB: this defaults to false in case we encounter any errors in retrieving TZ or this.data..
        let data = this.getData();
        if (data && data.isValid) {
            if (now.isBetween(data.from, data.to, 'minutes', '[]')) { //: NB: granularity set to minutes here, just in case these lines get executed a few milliseconds later and thus the seconds no longer match.. If the calls were done on second 59 though, this may still be a problem if by the time we check if NOW within range, as we could have moved onto the next minute on NOW time.
                this.lastChangedIncludedNow = true;
            }
        }

        // console.warn("DRP .setValues()", { in: this.lastChangedIncludedNow, tzn: tzd.timeZoneName, xx: $.extend(true, {}, data), f: from, t: to, ff: moment.isMoment(from) && from.format(DateRangePicker.MOMENT_DEBUG_FORMAT), tf: moment.isMoment(to) && to.format(DateRangePicker.MOMENT_DEBUG_FORMAT), nf: now.format(DateRangePicker.MOMENT_DEBUG_FORMAT), src: updateSource });

        this.preventUpdateEvents = false;

        if (runPostUpdateEvent) {
            this.triggerUpdate(updateSource);
        }

        this.onChangeDateViaSetValuesMethods = false;

        return this.getData();
    }

    /**
     * Automatically update the current range to include the now moment (adapted to selected timezone!).
     * Note: if current range already includes the now moment, then this will simply return the result of .getData() without any modifications (so no updates/callbacks are executed either!).
     * @param method "shift" or "extend". If "extend" is selected, then the range is extended (so TO extended to NOW if NOW is after TO, FROM extended to NOW if NOW is before FROM). 'shift" calculates the difference and appropriately shifts the range FROM/TO to include NOW, either at FROM or TO accordingly.
     * @param runPostUpdateEvent
     * @param updateSource
     */
    public autoIncludeNow(method: "shift" | "extend" = "extend", runPostUpdateEvent: boolean = true, updateSource: string = null): DateRangePickerData | boolean {
        let current: DateRangePickerData = this.getData();

        if (current && current.isValid) {
            let tzd: ITimeZoneData = this.getSelectedTimezoneData();
            let now: moment.Moment = moment.utc();
            if (tzd) {
                now = moment.utc().tz(tzd.timeZoneName);
            }

            // console.info("DRP.autoIncludeNow()", { x: now.format(DateRangePicker.MOMENT_DEBUG_FORMAT), xi: now.isBetween(current.from, current.to, 'seconds', '[]'), z: tzd.timeZoneName, f: current.from.format(DateRangePicker.MOMENT_DEBUG_FORMAT), t: current.to.format(DateRangePicker.MOMENT_DEBUG_FORMAT) });

            if (now.isBetween(current.from, current.to, 'seconds', '[]')) {
                return current;
            } else if (now.isBefore(current.from)) {
                let _to = current.to.clone();
                if (method == "shift") {
                    let diff = current.from.diff(now, 'milliseconds');
                    _to.subtract(diff, 'milliseconds');

                    // console.info("DRP.autoIncludeNow() [SHIFT/before]", { diff: diff, x: now.format(DateRangePicker.MOMENT_DEBUG_FORMAT), z: tzd.timeZoneName, c_f: current.from.format(DateRangePicker.MOMENT_DEBUG_FORMAT), c_t: current.to.format(DateRangePicker.MOMENT_DEBUG_FORMAT), n_f: now.format(DateRangePicker.MOMENT_DEBUG_FORMAT), n_t: _to.format(DateRangePicker.MOMENT_DEBUG_FORMAT) });
                }

                return this.setValues(now, _to, runPostUpdateEvent, updateSource);
            } else if (now.isAfter(current.to)) {
                let _from = current.from.clone();
                if (method == "shift") {
                    let diff = now.diff(current.to, 'milliseconds');
                    _from.add(diff, 'milliseconds');

                    // console.info("DRP.autoIncludeNow() [SHIFT/after]", { diff: diff, x: now.format(DateRangePicker.MOMENT_DEBUG_FORMAT), z: tzd.timeZoneName, c_f: current.from.format(DateRangePicker.MOMENT_DEBUG_FORMAT), c_t: current.to.format(DateRangePicker.MOMENT_DEBUG_FORMAT), n_f: _from.format(DateRangePicker.MOMENT_DEBUG_FORMAT), n_t: now.format(DateRangePicker.MOMENT_DEBUG_FORMAT) });
                }
                return this.setValues(_from, now, runPostUpdateEvent, updateSource);
            } else {
                console.error("DateRangePicker ERROR: unable to automatically include NOW as it's impossible to determine which direction the range should be extended.");
            }
        }
    }

    /**
     * Trigger the update for the date range picker (should call the update callbacks)
     */
    public triggerUpdate(updateSource: string = null): void {
        if (!this.rendered) {
            console.error("DateRangePicker ERROR: the elements have not yet been rendered. Please render them prior to triggering update.");
            return;
        }

        this.lastUpdateSource = updateSource;

        if ($.isFunction(this.postUpdateEvent) && !this.preventUpdateEvents) {
            this.postUpdateEvent(this, updateSource);
        }
    }

    public render(label: string = "Date&nbsp;Range", rowClass: string = "form-group", hideTimezoneSelect: boolean = false): void {
        let $row: JQuery = null, $col: JQuery = null;
        $row = $('<div>').addClass(rowClass).appendTo(this.$container);

        let colsLabel: number = 2;
        let colsDates: number = 6;
        let colsTzSel: number = 4;

        if (label !== null && label !== "") {
            $(`<label class="col-sm-${colsLabel} control-label">${label}</label>`).appendTo($row);
        } else {
            colsLabel = 0;
            colsDates = 8;
        }

        if (hideTimezoneSelect) {
            colsTzSel = 0;
            colsDates = 12 - colsLabel;
        }

        let $dpWrap: JQuery = $('<div class="input-daterange input-group input-group-sm"></div>');
        this.$inputFrom = $('<input class="form-control date-range-picker--from" type="text" name="start" placeholder="YYYY-MM-DD">').appendTo($dpWrap);
        let $divider: JQuery = $('<span class="input-group-addon">to</span>').appendTo($dpWrap);
        this.$inputTo = $('<input class="form-control date-range-picker--to" type="text" name="end" placeholder="YYYY-MM-DD">').appendTo($dpWrap);

        $col = $(`<div class="col-sm-${colsDates}"></div>`).appendTo($row);

        
        let inputOnChange = (e: JQueryEventObject) => {
            // console.info("[[DRP.input.on(change)]]...", e.originalEvent, e.target);
            if (!this.onChangeDateViaSetValuesMethods && this.options.onManualChangeDateUpdateTime) {
                if ($(e.target).hasClass('date-range-picker--from')) {
                    this.timeFrom = "00:00:00";
                }
                if ($(e.target).hasClass('date-range-picker--to')) {
                    this.timeTo = "23:59:59";
                }
            }
        };

        this.$inputTo.on("change", inputOnChange);
        this.$inputFrom.on("change", inputOnChange);
        // this.$inputTimezone.on("change", inputOnChange); //: added to individual handler below.

        this.$datepicker = $dpWrap.appendTo($col)
            .datepicker(<DatepickerOptions>{ //: https://bootstrap-datepicker.readthedocs.io/en/latest/options.html
                weekStart: 1,
                format: "yyyy-mm-dd",
                multidate: false,
                clearBtn: false,
                immediateUpdates: false,
                keepEmptyValues: true,
                todayBtn: false
            })
            .on('changeDate', (e) => {
                // console.info("DRP.datepicker.on(changeDate)", { event: e });

                //: auto-hide datepicker & move onto 'to' field if appropriate
                $(e.target).blur();
                $(e.target).datepicker('hide');
                if ($(e.target).hasClass("date-range-picker--from") && !this.preventUpdateEvents) {
                    this.$inputTo.trigger("focus");
                }

                this.dropdownHighlightItem(null);
                this.triggerUpdate('DateRangePicker.datepicker.on(changeDate)');
            });

        //: add timezone select:
        $col = $('<div>').addClass(hideTimezoneSelect ? 'hidden' : `col-sm-${colsTzSel}`).appendTo($row);
        this.$inputTimezone = $("<select>").addClass('form-control input-sm').on("change", (e) => {
            inputOnChange(e);
            this.triggerUpdate('DateRangePicker.[inputs_changes]');
        });
        this.$inputTimezone.appendTo($col);

        let tzOpts: ITimeZoneData[] = this.mainSite.getAllAvailableTimeZones();
        let $opt: JQuery = null;
        tzOpts.forEach(tzd => {
            $opt = $('<option>').attr('value', tzd.timeZoneName).text(tzd.label).data('tzOffsetMinutes', tzd.offsetMinutes).data('tzd', tzd);
            this.$inputTimezone.append($opt);
        });
        this.$inputTimezone.val(this.mainSite.getTimeZoneName());

        this.rendered = true;
    }

    public dropdownSetButtonText(text: string = null): void {
        if (this.$dropdownBtn) {
            let $span = this.$dropdownBtn.find('span.btn-text');
            if ($span.length == 0) {
                $span = $('<span>').addClass('btn-text').prependTo(this.$dropdownBtn);
            }

            $span.text(text);
        }
    }

    public dropdownSetButtonElements($el: JQuery = null): void {
        if (this.$dropdownBtn) {
            let $span = this.$dropdownBtn.find('span.btn-text');
            if ($span.length == 0) {
                $span = $('<span>').addClass('btn-text').prependTo(this.$dropdownBtn);
            }

            $span.empty().append($el);
        }
    }

    /**
     * Trigger the onClick event for the desired dropdown menu item, retrieved by its id.
     * @param id
     */
    public dropdownTriggerItemClick(id: string = null) {
        let $a = this.$dropdownWrap.find(`[data-id="${encodeURI(id)}"] a`);
        if ($a.length === 1) {
            $a.trigger("click");
        }
    }

    /**
     * Helper to highlight a specific dropdown menu item based on its id.
     * @param id
     */
    public dropdownHighlightItem(id: string = null) {
        this.$dropdownWrap.find(".active").removeClass("active");
        let $item = this.$dropdownWrap.find(`[data-id="${encodeURI(id)}"]`);
        if ($item.length === 1) {
            $item.addClass("active");
        }
    }

    public dropdownAddItem(id: string, label: string, onClick: (drp: DateRangePicker) => void, className: string = null, iconClassName: string = null): JQuery {
        if (this.$dropdownWrap === null) {
            this.$dropdownWrap = $('<ul class="dropdown-menu dropdown-menu-right">');

            let $btnGroup = $('<div class="input-group-btn">').insertAfter(this.$inputTo);
            this.$dropdownBtn = $('<button type="button" class="btn btn-default dropdown-toggle" data-toggle="dropdown"><i class="fa fa-fw fa-caret-down"></i></button>');
            $btnGroup.append(this.$dropdownBtn);
            this.$dropdownWrap.appendTo($btnGroup);
        }

        let $item: JQuery = null;

        if (id == "separator") {
            $item = $('<li role="separator" class="divider"></li>');
        }

        if (id == "header") {
            $item = $('<li class="dropdown-header"></li>').append($('<span>').text(label));
            if (iconClassName !== null && $item !== null) {
                $('<i>').addClass("fa fa-fw").addClass(iconClassName).css({ marginRight: 5 }).prependTo($item);
            }
        }

        if ($item == null) {
            let $a: JQuery = $('<a>').attr("href", "#").append($('<span>').text(label));
            $item = $('<li>').addClass("dropdown-menu-item").attr("data-id", id).append($a);
            if (className) {
                $item.addClass(className);
            }

            $a.on("click", (e) => {
                e.preventDefault();

                let disabled: boolean = ($a.hasClass('disabled') || $item.hasClass('disabled'));
                if (!disabled) {
                    this.lastClickedDropdownItemId = id;
                    this.dropdownHighlightItem(id);

                    if ($.isFunction(onClick)) {
                        onClick(this);
                    }
                }
            });

            if (iconClassName !== null && $item !== null) {
                $('<i>').addClass("fa fa-fw").addClass(iconClassName).css({ marginRight: 5 }).prependTo($a);
            }
        }

        $item.appendTo(this.$dropdownWrap);

        return $item;
    }
}
 